import React, { useEffect, useState, useCallback } from "react";
import { MdCheckCircle, MdOutlineRadioButtonUnchecked, MdContentCopy, MdExpandMore, MdEdit, MdDelete } from "react-icons/md";
import toast from "react-hot-toast";
import api from "../../../services/api";
import { mapActivitiesToOccupations } from "../../../utils/occupation";

export default function Questions({ user }) {
  const [result, setResult] = useState([]);
  const [copied, setCopied] = useState(false);
  const [isAnyLoading, setIsAnyLoading] = useState(false);
  const [showXml, setShowXml] = useState(false);
  const [dataSourceCounts, setDataSourceCounts] = useState({});
  const [prompt, setPrompt] = useState("");
  const [selectedPromptType, setSelectedPromptType] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [xml, setXml] = useState("");
  const [questions, setQuestions] = useState([]);
  const [editableQuestions, setEditableQuestions] = useState("");

  useEffect(() => {
    if (user && user.prompts && user.prompts.length > 0) {
      const firstPrompt = user.prompts[0];
      setSelectedPromptType(firstPrompt.name);
      setPrompt(firstPrompt.content);
    }
  }, [user]);

  function handlePromptTypeChange(value) {
    setSelectedPromptType(value);
    const selectedPrompt = (user.prompts || []).find((p) => p.name === value);
    if (selectedPrompt) {
      setPrompt(selectedPrompt.content);
    } else {
      setPrompt("");
    }
  }

  async function handleGenerateQuestions() {
    if (!prompt.trim()) return toast.error("Please enter a prompt");

    setIsLoading(true);
    try {
      const { ok, data } = await api.post("/meeting_question/generate", { prompt: prompt, xml: xml });
      if (!ok) return toast.error("Failed to generate questions");
      toast.success("Questions generated successfully!");
      setQuestions(data);
      setEditableQuestions(data.map((q) => `${q.text}`).join("\n\n"));
    } catch (error) {
      console.error("Error generating questions:", error);
      toast.error("Failed to generate questions");
    } finally {
      setIsLoading(false);
    }
  }

  const handleAddXml = useCallback(
    (xml, type, count = 0) => {
      const today = new Date().toISOString().split("T")[0];
      const xmlWithContext = `<today_date>${today}</today_date>\n${xml}`;
      setResult((prev) => [...prev, { xml: xmlWithContext, type }]);
      setDataSourceCounts((prev) => ({ ...prev, [type]: count }));
      setXml((result || []).map((e) => e.xml).join("\n") + "\n" + xmlWithContext);
    },
    [result],
  );

  const handleLoadingChange = useCallback((isLoading) => {
    setIsAnyLoading(isLoading);
  }, []);

  function handleCopy() {
    const text = (result || []).map((e) => e.xml).join("\n");
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success("XML copied to clipboard.");
    setTimeout(() => setCopied(false), 1200);
  }

  const completedSteps = result.length;
  const totalSteps = 6;

  return (
    <div className="max-w-full mx-auto ">
      <div className="bg-white rounded-lg shadow flex flex-row ">
        <div className="w-1/3 p-4 border-r border-gray-200 flex flex-col">
          <div className="flex justify-between items-center mb-3">
            <div className="flex items-center gap-3">
              <h3 className="text-base font-semibold">Data Sources</h3>
              <div className="flex items-center gap-2">
                <div className="w-12 bg-blue-200 rounded-full h-1">
                  <div className="bg-blue-600 h-1 rounded-full transition-all duration-300" style={{ width: `${(completedSteps / totalSteps) * 100}%` }}></div>
                </div>
                <span className="text-xs text-blue-600">
                  {completedSteps}/{totalSteps}
                </span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {isAnyLoading && (
                <div className="flex items-center gap-1 text-blue-600 text-xs">
                  <MdOutlineRadioButtonUnchecked className="animate-spin text-xs" />
                  Loading...
                </div>
              )}
            </div>
          </div>

          {/* Data Source Cards */}
          <div className="grid grid-cols-1 gap-2 mb-3">
            <DataSourceCard
              title="User Info"
              component={<UserInfoXml user={user} onAdd={handleAddXml} onLoadingChange={handleLoadingChange} />}
              count={dataSourceCounts.user || 0}
              description="Basic profile data"
            />
            <DataSourceCard
              title="Projects"
              component={<ProjectsXml user={user} onAdd={handleAddXml} onLoadingChange={handleLoadingChange} />}
              count={dataSourceCounts.projects || 0}
              description="Active projects"
            />
            <DataSourceCard
              title="Skills"
              component={<SkillsXml user={user} onAdd={handleAddXml} onLoadingChange={handleLoadingChange} />}
              count={dataSourceCounts.skills || 0}
              description="Technical skills"
            />
            <DataSourceCard
              title="Meeting Notes"
              component={<MeetingNotesXml user={user} onAdd={handleAddXml} onLoadingChange={handleLoadingChange} />}
              count={dataSourceCounts.notes || 0}
              description="Previous meetings"
            />
            <DataSourceCard
              title="Occupations"
              component={<OccupationsXml user={user} onAdd={handleAddXml} onLoadingChange={handleLoadingChange} />}
              count={dataSourceCounts.occupations || 0}
              description="Time allocation"
            />
            <DataSourceCard
              title="Budget Issues"
              component={<BudgetIssuesXml user={user} onAdd={handleAddXml} onLoadingChange={handleLoadingChange} />}
              count={dataSourceCounts.issues || 0}
              description="Current issues"
            />
          </div>

          {/* Collapsible XML Section */}
          <div className="mb-2">
            <div className="flex items-center justify-between mb-1">
              <button onClick={() => setShowXml(!showXml)} className="flex items-center gap-1 text-xs text-gray-600 hover:text-gray-800">
                <MdExpandMore className={`transition-transform text-xs ${showXml ? "rotate-180" : ""}`} />
                {showXml ? "Hide" : "Show"} XML Data
              </button>
              <button
                className={`flex items-center gap-1 px-1.5 py-0.5 rounded text-xs bg-gray-200 hover:bg-gray-300 transition ${copied ? "text-green-600" : "text-gray-700"}`}
                onClick={handleCopy}
                aria-label="Copy XML"
                type="button">
                <MdContentCopy className="inline text-xs" />
                {copied ? "Copied!" : "Copy"}
              </button>
            </div>
            {showXml && <pre className="text-xs bg-gray-50 p-2 rounded max-h-48 overflow-auto border">{(result || []).map((e) => e.xml).join("\n")}</pre>}
          </div>
        </div>
        <div className="w-2/3 p-6">
          <div className="h-full flex flex-col">
            <div className="flex-1 flex flex-col">
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Meeting Type</label>
                <select
                  value={selectedPromptType}
                  onChange={(e) => handlePromptTypeChange(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500">
                  {(user.prompts || []).map((prompt) => (
                    <option key={prompt.id} value={prompt.name}>
                      {prompt.name}
                    </option>
                  ))}
                </select>
              </div>
              <textarea
                className=" p-3 border border-gray-300 rounded-lg mb-4 text-sm "
                placeholder="Enter your prompt for generating questions..."
                value={prompt}
                rows={15}
                onChange={(e) => setPrompt(e.target.value)}
              />
              <button
                onClick={handleGenerateQuestions}
                disabled={isLoading || !prompt || !xml.trim()}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2">
                {isLoading && <MdOutlineRadioButtonUnchecked className="animate-spin" />}
                {isLoading ? "Generating..." : "Generate Questions"}
              </button>
              {!xml.trim() && <p className="text-xs text-gray-500 mt-2">Wait for data sources to load before generating questions</p>}
            </div>
          </div>
        </div>
      </div>
      {questions.length > 0 && (
        <div className="mt-4">
          <div className="flex justify-between items-center mb-2">
            <h4 className="text-md font-semibold">Generated Questions</h4>
            <button
              onClick={() => {
                navigator.clipboard.writeText(editableQuestions);
                toast.success("Questions copied to clipboard!");
              }}
              className="flex items-center gap-1 px-2 py-1 rounded text-xs bg-blue-100 hover:bg-blue-200 text-blue-700">
              <MdContentCopy className="inline text-xs" />
              Copy
            </button>
          </div>
          <textarea
            className="w-full h-64 p-3 border border-gray-300 rounded-lg text-sm resize-none bg-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            value={editableQuestions}
            onChange={(e) => setEditableQuestions(e.target.value)}
            placeholder="Generated questions will appear here... You can edit them as needed."
          />
        </div>
      )}
    </div>
  );
}

function DataSourceCard({ title, component, count, description }) {
  return (
    <div className="p-2 border border-gray-200 rounded hover:shadow-sm transition-shadow">
      <div className="flex items-center justify-between mb-0.5">
        <div className="flex flex-row">
          <h4 className="text-xs font-medium text-gray-900 mr-4">{title}</h4>
          <span className="text-xs text-gray-500 mr-4">{description}</span>
          <span className="text-xs text-blue-600">{count}</span>
        </div>
        {component}
      </div>
    </div>
  );
}

function UserInfoXml({ user, onAdd, onLoadingChange }) {
  const [isLoading, setLocalLoading] = useState(false);

  useEffect(() => {
    if (user && user._id) {
      generateUserInfoXml();
    }
  }, [user]);

  function generateUserInfoXml() {
    setLocalLoading(true);
    onLoadingChange(true);
    try {
      const xml = `<user_info>
  <id>${escapeXml(user._id)}</id>
  <name>${escapeXml(user.name)}</name>
  <email>${escapeXml(user.email)}</email>
  <position>${escapeXml(user.position)}</position>
  <job_title>${escapeXml(user.job_title)}</job_title>
  <contract>${escapeXml(user.contract)}</contract>
  <office>${escapeXml(user.office)}</office>
  <manager_name>${escapeXml(user.manager_name)}</manager_name>
  <main_objective>${escapeXml(user.main_objective)}</main_objective>
</user_info>`;
      onAdd(xml, "user", 1);
    } catch (error) {
      console.error("Error generating user info XML:", error);
      onAdd("No user info found.", "user", 0);
    } finally {
      setLocalLoading(false);
      onLoadingChange(false);
    }
  }

  return (
    <div className={`text-xs px-2 py-1 rounded flex items-center gap-1 ${isLoading ? "bg-blue-100 text-blue-700" : "bg-green-100 text-green-700"}`}>
      {isLoading ? <MdOutlineRadioButtonUnchecked className="animate-spin" /> : <MdCheckCircle />}
    </div>
  );
}

function ProjectsXml({ user, onAdd, onLoadingChange }) {
  const [isLoading, setLocalLoading] = useState(false);

  useEffect(() => {
    if (user && user._id) {
      fetchProjects();
    }
  }, [user]);

  async function fetchProjects() {
    setLocalLoading(true);
    onLoadingChange(true);
    try {
      const { ok, data } = await api.post("/project/search", {
        $or: [{ lead_id: user._id }, { lead_dev_id: user._id }, { lead_product_id: user._id }],
      });

      if (!ok) {
        console.error("API returned ok: false");
        onAdd("No projects found.", "projects", 0);
        return;
      }

      const xml = generateProjectsXml(data.projects);
      onAdd(xml, "projects", data.projects?.length || 0);
    } catch (error) {
      console.error("Error fetching projects:", error);
      onAdd("No projects found.", "projects", 0);
    } finally {
      setLocalLoading(false);
      onLoadingChange(false);
    }
  }

  function generateProjectsXml(projects) {
    if (!projects || projects.length === 0) return "No projects found.";

    const projectsXml = projects
      .map(
        (project) => `  <project>
    <name>${escapeXml(project.name)}</name>
    <description>${escapeXml(project.description || "")}</description>
    <lead_name>${escapeXml(project.lead_name || "")}</lead_name>
    <lead_product_name>${escapeXml(project.lead_product_name || "")}</lead_product_name>
    <lead_dev_name>${escapeXml(project.lead_dev_name || "")}</lead_dev_name>
    <status>${escapeXml(project.status || "")}</status>
  </project>`,
      )
      .join("\n");

    return `<projects>
${projectsXml}
</projects>`;
  }

  return (
    <div className={`text-xs px-2 py-1 rounded flex items-center gap-1 ${isLoading ? "bg-blue-100 text-blue-700" : "bg-green-100 text-green-700"}`}>
      {isLoading ? <MdOutlineRadioButtonUnchecked className="animate-spin" /> : <MdCheckCircle />}
    </div>
  );
}

function SkillsXml({ user, onAdd, onLoadingChange }) {
  const [isLoading, setLocalLoading] = useState(false);

  useEffect(() => {
    if (user) {
      generateSkillsXml();
    }
  }, [user]);

  function generateSkillsXml() {
    setLocalLoading(true);
    onLoadingChange(true);
    try {
      const xml = generateUserSkillsXml(user.skills || []);
      onAdd(xml, "skills", user.skills?.length || 0);
    } catch (error) {
      console.error("Error generating skills XML:", error);
      onAdd("No skills found.", "skills", 0);
    } finally {
      setLocalLoading(false);
      onLoadingChange(false);
    }
  }

  function generateUserSkillsXml(skills) {
    if (!skills || skills.length === 0) return "No skills found.";

    const userSkillsXml = skills
      .map(
        (skill) => `    <skill>
      <name>${escapeXml(skill.name)}</name>
      <category>${escapeXml(skill.category)}</category>
      <level>${skill.level || 0} / 4</level>
    </skill>`,
      )
      .join("\n");

    return `<skills>
  <user_skills>${userSkillsXml}
  </user_skills>
</skills>`;
  }

  return (
    <div className={`text-xs px-2 py-1 rounded flex items-center gap-1 ${isLoading ? "bg-blue-100 text-blue-700" : "bg-green-100 text-green-700"}`}>
      {isLoading ? <MdOutlineRadioButtonUnchecked className="animate-spin" /> : <MdCheckCircle />}
    </div>
  );
}

function MeetingNotesXml({ user, onAdd, onLoadingChange }) {
  const [isLoading, setLocalLoading] = useState(false);

  useEffect(() => {
    if (user && user._id) {
      fetchMeetingNotes();
    }
  }, [user]);

  async function fetchMeetingNotes() {
    setLocalLoading(true);
    onLoadingChange(true);
    try {
      const { ok, data } = await api.post("/meeting_note/search", { user_id: user._id });
      if (!ok) throw new Error("Failed to fetch meeting notes");

      const xml = generateNotesXml(data);
      onAdd(xml, "notes", data?.length || 0);
    } catch (error) {
      console.error("Error fetching meeting notes:", error);
      onAdd("No meeting notes found.", "notes", 0);
    } finally {
      setLocalLoading(false);
      onLoadingChange(false);
    }
  }

  function generateNotesXml(notes) {
    if (!notes || notes.length === 0) return "No meeting notes found.";

    const notesXml = notes
      .map(
        (note) => `  <note>
    <text>${escapeXml(note.text)}</text>
    <created_at>${note.created_at ? new Date(note.created_at).toISOString() : ""}</created_at>
  </note>`,
      )
      .join("\n");

    return `<recent_notes>
${notesXml}
</recent_notes>`;
  }

  return (
    <div className={`text-xs px-2 py-1 rounded flex items-center gap-1 ${isLoading ? "bg-blue-100 text-blue-700" : "bg-green-100 text-green-700"}`}>
      {isLoading ? <MdOutlineRadioButtonUnchecked className="animate-spin" /> : <MdCheckCircle />}
    </div>
  );
}

function OccupationsXml({ user, onAdd, onLoadingChange }) {
  const [isLoading, setLocalLoading] = useState(false);

  useEffect(() => {
    if (user && user._id) {
      fetchOccupations();
    }
  }, [user]);

  async function fetchOccupations() {
    setLocalLoading(true);
    onLoadingChange(true);
    try {
      const { ok, data } = await api.post("/activity/search", { userId: user._id, has_forecast: true });
      if (!ok) throw new Error("Failed to fetch occupations");

      const occupations = mapActivitiesToOccupations(data || []);
      const xml = generateOccupationsXml(occupations);
      onAdd(xml, "occupations", occupations?.length || 0);
    } catch (error) {
      console.error("Error fetching occupations:", error);
      onAdd("No occupations found.", "occupations", 0);
    } finally {
      setLocalLoading(false);
      onLoadingChange(false);
    }
  }

  function generateOccupationsXml(occupations) {
    if (!occupations || occupations.length === 0) return "No occupations found.";

    const occupationsXml = occupations
      .map(
        (occupation) => `  <occupation>
    <month>${escapeXml(occupation.month ? new Date(occupation.month).toLocaleDateString("fr-FR", { month: "long", year: "numeric" }) : "")}</month>
    <project_name>${escapeXml(occupation.project_name)}</project_name>
    <percent>${occupation.percent || 0}</percent>
  </occupation>`,
      )
      .join("\n");

    return `<occupations>
${occupationsXml}
</occupations>`;
  }

  return (
    <div className={`text-xs px-2 py-1 rounded flex items-center gap-1 ${isLoading ? "bg-blue-100 text-blue-700" : "bg-green-100 text-green-700"}`}>
      {isLoading ? <MdOutlineRadioButtonUnchecked className="animate-spin" /> : <MdCheckCircle />}
    </div>
  );
}

function BudgetIssuesXml({ user, onAdd, onLoadingChange }) {
  const [isLoading, setLocalLoading] = useState(false);

  useEffect(() => {
    if (user && user._id) {
      fetchBudgetIssues();
    }
  }, [user]);

  async function fetchBudgetIssues() {
    setLocalLoading(true);
    onLoadingChange(true);
    try {
      const { ok, data } = await api.post("/budget_check/analyze", { user_id: user._id });
      if (!ok) throw new Error("Failed to fetch budget issues");

      const xml = generateBudgetIssuesXml(data);
      onAdd(xml, "issues", data?.length || 0);
    } catch (error) {
      console.error("Error fetching budget issues:", error);
      onAdd("No budget issues found.", "issues", 0);
    } finally {
      setLocalLoading(false);
      onLoadingChange(false);
    }
  }

  function generateBudgetIssuesXml(budgetIssues) {
    if (!budgetIssues || budgetIssues.length === 0) return "No budget issues found.";

    const issuesXml = budgetIssues
      .map(
        (issue) => `  <issue>
    <project_name>${escapeXml(issue.projectName || "")}</project_name>
    <problem>${escapeXml(issue.problem || "")}</problem>
    <action>${escapeXml(issue.action || "")}</action>
    <user_name>${escapeXml(issue.userName || "")}</user_name>
    <link>${escapeXml(issue.link || "")}</link>
  </issue>`,
      )
      .join("\n");

    return `<budget_issues>
${issuesXml}
</budget_issues>`;
  }

  return (
    <div className={`text-xs px-2 py-1 rounded flex items-center gap-1 ${isLoading ? "bg-blue-100 text-blue-700" : "bg-green-100 text-green-700"}`}>
      {isLoading ? <MdOutlineRadioButtonUnchecked className="animate-spin" /> : <MdCheckCircle />}
    </div>
  );
}

function escapeXml(text) {
  if (!text) return "";
  return String(text).replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;").replace(/'/g, "&apos;");
}
